import {CheckItemsManager} from '@app/modules/diagnostics/components/diagnostics/diagnostic-items/managers/check-items-manager';
import {Step} from '@app/modules/diagnostics/components/diagnostics/diagnostic-items/managers/step';
import {TestPassState, TestQuestion, TestResponse, TestResponseCommands} from '@app/modules/diagnostics/components/diagnostics/diagnostic-items/models';
import {Observable} from 'rxjs';
import {Role} from '@app/modules/shared/data/constants';

const MEMORY_CONFIRMATION_STORAGE_KEY = 'memory_confirmation_expires_at';
const MEMORY_CONFIRMATION_VALID_DAYS = 30;

function isMemoryConfirmationRemembered(): boolean {
  const expiresAt = Number(localStorage.getItem(MEMORY_CONFIRMATION_STORAGE_KEY));
  return !!expiresAt && Date.now() < expiresAt;
}

function rememberMemoryConfirmation(): void {
  const expiresAt = Date.now() + MEMORY_CONFIRMATION_VALID_DAYS * 24 * 60 * 60 * 1000;
  localStorage.setItem(MEMORY_CONFIRMATION_STORAGE_KEY, String(expiresAt));
}

export class MemoryCheckManager extends CheckItemsManager {
  icon = 'memory-g';
  name = 'Memory';

  constructor(userRole: Role) {
    super(userRole);
    this.steps.push(new MemoryConfirmation(userRole));

    // Staff who confirmed within the last 30 days on this browser/device, and students
    // (for whom this check never applies), skip the tab entirely.
    if (userRole === Role.STUDENT || isMemoryConfirmationRemembered()) {
      this.isHidden = true;
      this.isBlocked = true;
    }
  }
}

export class MemoryConfirmation extends Step {
  public name = 'RAM check';
  public title = 'Memory validation…';
  public userRole: Role;

  constructor(userRole: Role) {
    super(true);
    this.userRole = userRole;
    this.testQuestion = new TestQuestion('Does your device have at least 16 GB of RAM?', [
      {text: 'No', value: 'no', color: 'blue', isBlock: false, isDisabled: false},
      {text: 'Yes', value: 'yes', color: 'blue', isBlock: false, isDisabled: false},
    ]);

    if (userRole !== Role.STUDENT && isMemoryConfirmationRemembered()) {
      this.testResult.status = TestPassState.PASSED;
      this.testResult.text = '16 GB+';
      this.testStatus = 'OK';
    }
  }

  doRunStep(): Observable<TestResponse> {
    return new Observable<TestResponse>(subscriber => {
      if (this.stepPayload === 'no') {
        this.testResult.status = TestPassState.FAILED;
        this.testResult.errorMassage = 'Your computer does not have enough memory, which may cause performance issues.' +
          ' You may need a different computer for future class sessions.';
        this.testResult.text = 'insufficient';
        this.testStatus = 'NO';
        subscriber.next(new TestResponse(TestResponseCommands.SHOW_ERROR));
      } else {
        this.testResult.status = TestPassState.PASSED;
        this.testResult.text = '16 GB+';
        this.testStatus = 'OK';
        rememberMemoryConfirmation();
        subscriber.next(new TestResponse(TestResponseCommands.NEXT));
      }
    });
  }

  override toString() {
    // Students never see this step, so it is left out of their diagnostics report.
    return this.userRole === Role.STUDENT ? '' : super.toString();
  }
}
